import 'primeicons/primeicons.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Catalog } from './pages/Catalog';
import { DocumentViewer } from './pages/DocumentViewer';
import { OAuthCallback } from './pages/OAuthCallback';
import { Forbidden } from './pages/Forbidden';
import { TestPantaris } from './pages/TestPantaris';
import { TestEtas } from './pages/TestEtas';
import { loadConfig } from './config';

function App() {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    // Load config first (always needed)
    loadConfig()
      .then(() => {
        setConfigLoaded(true);
        
        // After config loads, handle OAuth callback if present
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('callback') && urlParams.has('code')) {
          // This is an OAuth callback - clean up URL params without reload
          const code = urlParams.get('code');
          const state = urlParams.get('state');
          
          // Store params for the OAuthCallback component to use
          sessionStorage.setItem('oauth_callback_code', code || '');
          if (state) {
            sessionStorage.setItem('oauth_callback_state', state);
          }
          
          // Clean URL and let Router handle navigation to /auth/callback
          window.history.replaceState({}, '', '/auth/callback');
          // Force router to re-render with new path
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      })
      .catch((error) => {
        console.error('Failed to load config:', error);
        setConfigError(error.message);
      });

    // Clear any stale OAuth state if not in callback flow
    const urlParams = new URLSearchParams(window.location.search);
    if (!window.location.pathname.includes('/auth/callback')) {
      // Only clear if there's no active OAuth code parameter
      if (!urlParams.has('code')) {
        sessionStorage.removeItem('oauth_return_to');
      }
    }
  }, []);

  if (configError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1>Configuration Error</h1>
        <p>{configError}</p>
      </div>
    );
  }

  if (!configLoaded) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading configuration...</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Catalog />} />

        {/*
          Preview route. NOT legacy and NOT removable: this is the only URL shape that can
          carry a pinned git revision in :version, and CloudHub's "Open Preview" button
          links straight to it. The backend 302s the ':version === main' case to the
          bare-slug route below, but serves pinned revisions inline here, so React Router
          must still match it to pass the revision through to DocumentViewer.
        */}
        <Route path="/application/:uuid/:name/:version/*" element={<DocumentViewer />} />

        {/* OAuth & Test routes. Declared before the catch-all for readability only —
            React Router v6 ranks static segments above dynamic ones regardless of order,
            so these always win over /:appNameSlug/* below. */}
        <Route path="/auth/callback" element={<OAuthCallback />} />
        <Route path="/forbidden" element={<Forbidden />} />
        <Route path="/test/pantaris" element={<TestPantaris />} />
        <Route path="/test/etas" element={<TestEtas />} />

        {/*
          Production document route: /{slug}/{resourcePath}.

          Two consequences worth knowing before adding anything here:

          1. This is now the app's de-facto 404 surface. A splat matches an empty
             remainder, so EVERY unmatched path of one segment or more lands in
             DocumentViewer, which renders "Document Not Found" when the slug resolves to
             no application. There is no separate NotFound route, and adding a
             <Route path="*"> would be dead code.
          2. Any new top-level static route must also be added to the backend's reserved
             slug denylist (backend/src/utils/reserved-slugs.ts), otherwise the backend
             will happily resolve it as a document slug and the two routers will disagree
             about what the same URL means.
        */}
        <Route path="/:appNameSlug/*" element={<DocumentViewer />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
